import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getAccessToken, setAccessToken } from "../api/http";
import type { PlatformImpersonationRow } from "../api/types";

interface ImpersonationState {
  active: PlatformImpersonationRow | null;
  originalAccessToken: string | null;
  start: (resp: { access: string; refresh: string; impersonation: PlatformImpersonationRow }) => void;
  end: () => void;
  readOnlyReason: string | null;
}

const STORAGE_KEY = "adam-impersonation-session";

const Ctx = createContext<ImpersonationState | undefined>(undefined);

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [active, setActive] = useState<PlatformImpersonationRow | null>(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as PlatformImpersonationRow) : null;
    } catch {
      return null;
    }
  });
  const [originalAccessToken, setOriginalAccessToken] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(`${STORAGE_KEY}-orig`) ?? null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (active) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(active));
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // sessionStorage may throw in private mode / quota — non-fatal.
    }
  }, [active]);

  useEffect(() => {
    try {
      if (originalAccessToken) sessionStorage.setItem(`${STORAGE_KEY}-orig`, originalAccessToken);
      else sessionStorage.removeItem(`${STORAGE_KEY}-orig`);
    } catch {
      // sessionStorage may throw in private mode / quota — non-fatal.
    }
  }, [originalAccessToken]);

  const start: ImpersonationState["start"] = useCallback(
    ({ access, impersonation }) => {
      const current = getAccessToken();
      if (current) setOriginalAccessToken(current);
      setAccessToken(access);
      setActive(impersonation);
      // The active user just changed — purge per-user caches (business, me,
      // permissions, dashboards, etc.) so every query refetches under the new
      // identity instead of serving stale data from the previous session.
      void queryClient.resetQueries();
    },
    [queryClient]
  );

  const end: ImpersonationState["end"] = useCallback(() => {
    if (originalAccessToken) {
      setAccessToken(originalAccessToken);
    }
    setOriginalAccessToken(null);
    setActive(null);
    // Hard reload back to the platform overview. Surgical cache invalidation
    // leaves AppLayout/usePermissions holding the impersonated user's stale
    // business — a full reload guarantees a clean slate under the platform
    // admin identity. Auth refetches /me on mount so the user state is fresh.
    try {
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(`${STORAGE_KEY}-orig`);
    } catch {
      // sessionStorage may throw in private mode — non-fatal.
    }
    window.location.replace("/platform/overview");
  }, [originalAccessToken]);

  const value = useMemo<ImpersonationState>(
    () => ({
      active,
      originalAccessToken,
      start,
      end,
      readOnlyReason: active ? "Impersonating — read-only session." : null,
    }),
    [active, originalAccessToken, start, end]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useImpersonation(): ImpersonationState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useImpersonation must be used within ImpersonationProvider");
  return ctx;
}
